import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path, opts = {}) {
  return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', 'x-worker-token': getToken(), ...opts.headers } })
    .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
}

const EMPTY = { name: '', sku: '', category: '', unit: 'EACH', qty_on_hand: '', location: '', reorder_point: '', unit_cost: '', vendor: '', notes: '' };
const isLow = it => it.reorder_point != null && it.qty_on_hand != null && Number(it.qty_on_hand) <= Number(it.reorder_point);
const fmtMoney = n => n == null || n === '' ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// "Stock Items Recap" (Pat, Sep 2026): every stock item in one table — on hand, reorder
// level, location, cost, vendor — across the Stock Material ledgers (poles are their own table). It was a
// Settings tile called Stock Items; it now lives where the stock is worked, as the first
// tile in Shop → Material → Stock Material (`embedded`), and /admin/stock still opens it
// as a page for any old link. The office adds and edits here and sees cost; anyone else
// who reaches Stock Material (the field's Shop hub) gets the list to read.
const RECAP_OFFICE = ['admin', 'Admin', 'Executive', 'Accounting', 'OfficeStaff', 'ProjectManager'];
function recapRole() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.role || ''; } catch { return ''; } }

export default function AdminStock() { return <StockRecapContent />; }

export function StockRecapContent({ embedded = false }) {
  const Wrap = embedded ? React.Fragment : AdminLayout;
  const office = RECAP_OFFICE.includes(recapRole());
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState(null); // 'new' | id | null
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { apiFetch('/stock-items').then(setItems).catch(() => {}).finally(() => setLoading(false)); }, []);

  function startAdd() { setEditId('new'); setForm(EMPTY); setErr(''); }
  function startEdit(it) { setEditId(it.id); setForm(Object.fromEntries(Object.keys(EMPTY).map(k => [k, it[k] ?? '']))); setErr(''); }
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function save() {
    if (!form.name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    try {
      const isNew = editId === 'new';
      const saved = await apiFetch(isNew ? '/stock-items' : `/stock-items/${editId}`, { method: isNew ? 'POST' : 'PATCH', body: JSON.stringify(form) });
      setItems(prev => (isNew ? [...prev, saved] : prev.map(x => x.id === saved.id ? saved : x)).sort((a, b) => a.name.localeCompare(b.name)));
      setEditId(null);
    } catch (e) { setErr(e.message || 'Save failed'); }
    setSaving(false);
  }
  async function del(id) {
    if (!window.confirm('Delete this stock item?')) return;
    try { await apiFetch(`/stock-items/${id}`, { method: 'DELETE' }); setItems(prev => prev.filter(x => x.id !== id)); } catch {}
  }

  const filtered = items.filter(it => { const q = search.trim().toLowerCase(); return !q || `${it.name} ${it.sku || ''} ${it.category || ''} ${it.location || ''} ${it.vendor || ''}`.toLowerCase().includes(q); });
  const lowCount = items.filter(isLow).length;
  const inp = { border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 9px', fontSize: 13, width: '100%', boxSizing: 'border-box' };

  return (
    <Wrap>
      <div className="admin-page-header">
        <div>
          {!embedded && (
            <button onClick={() => navigate('/admin/shop?tab=material&stock=1')}
              style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 6 }}>‹ Stock Material</button>
          )}
          <h1 className="admin-page-title" style={embedded ? { fontSize: 20 } : undefined}>Stock Items Recap</h1>
          <p className="admin-page-sub">{items.length} item{items.length !== 1 ? 's' : ''}{lowCount > 0 ? ` · ${lowCount} low on stock` : ''} · wire, pipe &amp; conduit, boxes and miscellaneous in one list (poles have their own tile)</p>
        </div>
        {office && <button className="admin-primary-btn" onClick={startAdd}>+ Add Item</button>}
      </div>

      <div className="admin-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, SKU, category, location, vendor…"
            style={{ ...inp, maxWidth: 380 }} />
        </div>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>{items.length === 0 ? 'No stock items yet — add your first.' : 'No matches.'}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Item</th><th>SKU</th><th>Category</th><th>On Hand</th><th>Reorder</th><th>Unit</th><th>Location</th>{office && <th>Unit Cost</th>}<th>Vendor</th>{office && <th style={{ width: 70 }}></th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map(it => (
                  <tr key={it.id} style={{ background: isLow(it) ? '#fef2f2' : undefined }}>
                    <td style={{ fontWeight: 600 }}>{it.name}</td>
                    <td className="admin-list-secondary">{it.sku || '—'}</td>
                    <td className="admin-list-secondary">{it.category || '—'}</td>
                    <td>
                      <span style={{ fontWeight: 700, color: isLow(it) ? '#b91c1c' : '#111827' }}>{it.qty_on_hand ?? 0}</span>
                      {isLow(it) && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#b91c1c', background: '#fee2e2', padding: '1px 6px', borderRadius: 8 }}>LOW</span>}
                    </td>
                    <td className="admin-list-secondary">{it.reorder_point ?? '—'}</td>
                    <td className="admin-list-secondary">{it.unit || '—'}</td>
                    <td className="admin-list-secondary">{it.location || '—'}</td>
                    {office && <td className="admin-list-secondary">{fmtMoney(it.unit_cost)}</td>}
                    <td className="admin-list-secondary">{it.vendor || '—'}</td>
                    {office && (
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button onClick={() => startEdit(it)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1d4ed8', fontSize: 12, fontWeight: 600 }}>Edit</button>
                        <button onClick={() => del(it.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 15, marginLeft: 4 }}>×</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setEditId(null)}>
          <div style={{ background: '#fff', borderRadius: 14, width: 560, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 12px 48px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <div style={{ background: '#b45309', borderRadius: '14px 14px 0 0', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{editId === 'new' ? 'Add Stock Item' : 'Edit Stock Item'}</span>
              <button onClick={() => setEditId(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                ['Name *', 'name', 'text'], ['SKU / Part #', 'sku', 'text'],
                ['Category', 'category', 'text'], ['Unit', 'unit', 'text'],
                ['Qty On Hand', 'qty_on_hand', 'number'], ['Reorder Point', 'reorder_point', 'number'],
                ['Location / Yard', 'location', 'text'], ['Unit Cost ($)', 'unit_cost', 'number'],
                ['Vendor', 'vendor', 'text'],
              ].map(([label, key, type]) => (
                <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <label style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>{label}</label>
                  <input type={type} value={form[key]} onChange={e => set(key, e.target.value)} style={inp} />
                </div>
              ))}
              <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 3 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Notes</label>
                <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              {err && <div style={{ gridColumn: '1 / -1', color: '#dc2626', fontSize: 13 }}>{err}</div>}
              <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
                <button onClick={() => setEditId(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button onClick={save} disabled={saving} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Wrap>
  );
}
